import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CcoHttpUrlGenerator } from '@app/store/cco-http-url-generator';
import {
  DefaultDataServiceConfig,
  EntityActionFactory,
  EntityDefinitionService,
  EntityOp,
  EntityServices,
  MergeStrategy,
  QueryParams,
} from '@ngrx/data';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject } from 'rxjs';
import { Page, PageInfo } from '@cco/model';
import { map, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IdSelector } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { selectGlobalFilterDates } from '@app/store/reducers';
import { CcoEntityMetadata } from '@app/data/entity-metadata';

export interface GetOptions {
  startPage?: number;
  query?: QueryParams;
  pageSize?: number;
  maxPages?: number;
}

export interface AddActionPayload {
  pageInfo: {
    query: string;
    lastPageOfGetWithQuery: boolean;
    lastPageOfGetAll: boolean;
  }
}

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class PaginatedEntityService {
  protected store = inject(Store);
  protected http = inject(HttpClient);
  protected httpUrlGenerator = inject(CcoHttpUrlGenerator);
  protected entityServices = inject(EntityServices);
  protected entityActionFactory = inject(EntityActionFactory);
  protected entityDefinitionService = inject(EntityDefinitionService);

  private baseUrl: string;
  loadingStateMap: {
    [entityName: string]: {
      [stringifiedOptions: string]: boolean
    }
  } = {};

  constructor() {
    const config = inject(DefaultDataServiceConfig);

    this.baseUrl = config?.root ?? 'api';
  }

  protected getPage<T>(entityName: string, page: number, size = 100, query?: QueryParams): Observable<Page<T>> {
    const params = new HttpParams({
      fromObject: {
        ...query,
        page: `${ page }`,
        size: `${ size }`,
      }
    });
    return this.httpUrlGenerator.collectionResource$(entityName, this.baseUrl).pipe(
      switchMap(entitiesUrl =>
        this.http.get<Page<T>>(entitiesUrl, { params }),
      ),
    );
  }

  public getWithPagination<T>(entityName: string, options: GetOptions = {}, ignoreGlobalFilter = false): Observable<T[]> {
    const { startPage = 0, query, pageSize = 100, maxPages } = options;

    const page$ = new BehaviorSubject<number>(startPage);
    const done$ = new Subject<void>();
    const fetchedContent: T[] = [];
    const fetchedIds = [];
    const selectId = this.entityDefinitionService.getDefinition(entityName).selectId;

    const loadingKey = this.generateLoadingKeyFromOptions(options);

    if (this.loadingStateMap[entityName]?.[loadingKey] === true)
      return EMPTY;

    if (this.loadingStateMap[entityName] == null)
      this.loadingStateMap[entityName] = {};

    this.loadingStateMap[entityName][loadingKey] = true;

    const results$: Observable<Page<T>> = combineLatest([
      page$,
      this.currentEntityIds$(entityName, selectId),
      this.store.select((selectGlobalFilterDates)),
    ]).pipe(
      map(([page, existingIds, globalFilterState]) => {
        if (!ignoreGlobalFilter && this.globalFilterApplies(entityName)) {
          const filteredGlobalFilters = Object.fromEntries(Object.entries(globalFilterState).filter(([, value]) => value != null));
          return [page, existingIds, {...query, ...(filteredGlobalFilters)}];
        }
        return [page, existingIds, query];
      }),
      mergeMap(([ page, existingIds, query ]: [number, number[], QueryParams]) =>
        this.getPage<T>(entityName, page, pageSize, query).pipe(
          tap((pagedResponse) => {
            const { content, ...pageInfo } = pagedResponse;
            if (content) {
              fetchedIds.push(...content.map(entity => selectId(entity)));

              const addAction = this.entityActionFactory.create(
                entityName,
                EntityOp.QUERY_MANY_SUCCESS,
                pagedResponse.content,
              );
              (addAction.payload as any).pageInfo = pageInfo;

              if (this.isLastPageOfGetWithQuery(options, pageInfo)) {
                (addAction.payload as unknown as AddActionPayload).pageInfo.lastPageOfGetWithQuery = true;
                (addAction.payload as unknown as AddActionPayload).pageInfo.query = JSON.stringify(query);
              } else if (this.isLastPageOfGetAll(options, pageInfo)) {
                (addAction.payload as unknown as AddActionPayload).pageInfo.lastPageOfGetAll = true;
              }
              this.entityServices.dispatch(addAction);
              fetchedContent.push(...pagedResponse.content);
            }

            if (this.isLastPageOfGetAll(options, pageInfo)) {
              this.removeEntitiesFromCacheThatWereNotRetrieved(entityName, existingIds, fetchedIds);
            }

            if (pagedResponse.last === true || this.isLastRequestedPage(page, maxPages)) {
              this.loadingStateMap[entityName][loadingKey] = false;
              done$.next();
              done$.complete();
            } else {
              page$.next(page + 1);
            }
          }),
        ),
      ),
      takeUntil(done$),
      untilDestroyed(this),
    );

    results$.subscribe();

    return done$.pipe(
      map(() => fetchedContent),
    );
  }

  public getAll(entityName: string) {
    this.getWithPagination(entityName);
  }

  protected isGetAll(options: GetOptions): boolean {
    const { startPage = 0, query, maxPages } = options;

    return startPage === 0 && (query == null || Object.keys(query).length === 0) && maxPages == null;
  }

  protected isGetWithQuery(options: GetOptions): boolean {
    const { query } = options;

    return query != null && Object.keys(query)?.length > 0;
  }

  protected isLastPageOfGetAll(options: GetOptions, pageInfo: PageInfo): boolean {
    return this.isGetAll(options) && pageInfo.last;
  }

  protected isLastPageOfGetWithQuery(options: GetOptions, pageInfo: PageInfo): boolean {
    return this.isGetWithQuery(options) && pageInfo.last;
  }

  protected isLastRequestedPage(page: number, maxPages?: number): boolean {
    return maxPages && (page + 1) === maxPages;
  }

  protected globalFilterApplies(entityName: string): boolean {
    return (this.entityDefinitionService.getDefinition(entityName)
      .metadata as CcoEntityMetadata)?.timeBased ?? false;
  }

  protected generateLoadingKeyFromOptions(options: GetOptions): string {
    return JSON.stringify({
      startPage: options.startPage ?? null,
      maxPages: options.maxPages ?? null,
      query: options.query ? Object.fromEntries(Object.entries(options.query).sort()) : null,
    });
  }

  protected removeEntitiesFromCacheThatWereNotRetrieved(entityName, existingIds, fetchedIds) {
    if (existingIds == null || existingIds.length === 0)
      return;

    const existingIdsNotRetrieved = [];
    for (const id of existingIds) {
      if (!fetchedIds.includes(id))
        existingIdsNotRetrieved.push(id);
    }

    if (existingIdsNotRetrieved.length > 0) {
      const removeAction = this.entityActionFactory.create(
        entityName,
        EntityOp.REMOVE_MANY,
        existingIdsNotRetrieved,
        {
          // Bypass EntityChangeTracker (see https://ngrx.io/guide/data/entity-change-tracker) since we are only calling one side of a tracked operation to clear data that has been removed on the remote side.
          mergeStrategy: MergeStrategy.IgnoreChanges,
        },
      );
      this.entityServices.dispatch(removeAction);
    }
  }

  protected currentEntityIds$<T>(entityName: string, selectId: IdSelector<T>): Observable<(string | number)[]> {
    return this.entityServices.getEntityCollectionService<T>(entityName).entities$.pipe(
      take(1),
      map(entities => entities?.map(entity => selectId(entity)) ?? []),
    );
  }
}
